// src/hooks/useAccessForm.js
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSupaContext } from "../contexts/SupaContext";

// Modificamos para hacerlo más flexible con diferentes parámetros
export const useAccessForm = ({ open, onClose, initialTab = "signin", redirectAfterLogin = true } = {}) => {
  const navigate = useNavigate();
  const { login, registerUser, user, isActiveUser } = useSupaContext();

  const [isRegistering, setIsRegistering] = useState(initialTab === "signup");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [phonePrefix, setPhonePrefix] = useState("+57");
  const [validationErrors, setValidationErrors] = useState({
    phone: "",
    document_value: "",
  });

  // Validaciones de contraseña
  const [validations, setValidations] = useState({
    hasUpperCase: false,
    hasNumber: false,
    minLength: false,
  });

  const [signInData, setSignInData] = useState({ email: "", password: "" });
  const [signUpData, setSignUpData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    nationality: "colombia",
    document_type: "national_id",
    document_value: "",
  });

  // Si estamos en el contexto de un modal
  useEffect(() => {
    if (user && open && onClose) onClose();
  }, [user, open, onClose]);

  // Si estamos en el contexto de una página (no un modal)
  useEffect(() => {
    // Solo redirigir si no estamos en un modal (cuando open es undefined)
    // Y SOLO SI redirectAfterLogin es true
    if (user && open === undefined && redirectAfterLogin) {
      navigate("/app");
    }
  }, [user, open, navigate, redirectAfterLogin]);

  // Actualizar validaciones de contraseña cuando cambia
  useEffect(() => {
    if (signUpData.password) {
      const hasUpperCase = /[A-Z]/.test(signUpData.password);
      const hasNumber = /[0-9]/.test(signUpData.password);
      const minLength = signUpData.password.length >= 8;
      
      setValidations({
        hasUpperCase,
        hasNumber,
        minLength,
      });
      
      if (!(hasUpperCase && hasNumber && minLength)) {
        setPasswordError("Debe tener al menos 8 caracteres, una mayúscula y un número");
      } else {
        setPasswordError("");
      }
    }
  }, [signUpData.password]);

  // Función para manejar cambios en campos
  const handleChange = (field, value) => {
    if (isRegistering || activeTab === "signup") {
      setSignUpData(prev => ({ ...prev, [field]: value }));
    } else {
      setSignInData(prev => ({ ...prev, [field]: value }));
    }
  };

  const validatePassword = (password) =>
    password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(signInData.email, signInData.password);
      // No necesitamos llamar a onClose explícitamente aquí, el useEffect se encargará
      // según el contexto en el que se use el hook
    } catch {
      setError("Correo o contraseña incorrectos");
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e, formattedPhone) => {
    e?.preventDefault();
    setError("");
    setPasswordError("");
    
    if (!validatePassword(signUpData.password)) {
      setPasswordError(
        "Debe tener al menos 8 caracteres, una mayúscula y un número"
      );
      return false;
    }
    
    if (!acceptTerms) {
      setError("Debes aceptar los términos y condiciones.");
      return false;
    }
  
    setLoading(true);
    try {
      const { email, password, ...userData } = signUpData;
      // Si se proporciona un teléfono formateado, úsalo en lugar del teléfono del signUpData
      if (formattedPhone) {
        userData.phone = formattedPhone;
      }
      
      const result = await registerUser(email, password, userData);
      
      if (!result.success) throw new Error(result.error);
      
      // No necesitamos llamar a onClose explícitamente aquí, el useEffect se encargará
      // según el contexto en el que se use el hook
      return true;
    } catch (error) {
      setError("Error al crear la cuenta. Verifica tus datos.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    isRegistering,
    setIsRegistering,
    activeTab,
    setActiveTab,
    loading,
    error,
    showPassword,
    setShowPassword,
    signInData,
    setSignInData,
    handleSignIn,
    signUpData,
    setSignUpData,
    handleSignUp,
    handleChange,
    passwordError,
    validations,
    acceptTerms,
    setAcceptTerms,
    phonePrefix,
    setPhonePrefix,
    validationErrors,
    isActiveUser
  };
};